// Booking price computation.
//
// Mirrors the backend at there-2-be-api/src/utils/bookingPricing.js exactly so
// the amount the brand sees here and the amount the server charges can never
// diverge. Keep the two in sync — the server derives the total from the Space's
// price and will not accept a client-computed figure.
//
//   fee   = clamp(price * 0.05, 1, 50)  ← There2B platform fee, bounded NET
//   total = (price + IVA(price)) + (fee + IVA(fee))
//
// ROUNDING — integer cents, half-up, each IVA rounded on its own line, exactly
// as the API's money.js does. The total is the promoter's invoice plus
// There2B's invoice, so the EasyPay split pays each party what their fatura
// says. Never compute it in float and round at the end: 30 + 1,50 + 7,245 is
// 38,74499… in IEEE-754 and rounds to 38,74 instead of 38,75, and the missing
// cent comes out of the promoter's slice (booking #1562, 2026-09-25).
//
// THE BOUNDS
//   The fee is 5% of the stand price but never less than €1 and never more than
//   €50, both net of VAT. The floor binds below price = 20, the ceiling from
//   price = 1000 up; between them this is the same arithmetic as before. VAT is
//   charged on top of the bounded fee, so a floored booking's fee invoice is
//   €1 + 23% = €1,23 and a capped one's is €61,50.
//
//     price     5 → fee  1,00 (20,00%)  total     7,38
//     price    20 → fee  1,00 ( 5,00%)  total    25,83
//     price   100 → fee  5,00 ( 5,00%)  total   129,15
//     price 1.000 → fee 50,00 ( 5,00%)  total 1.291,50
//     price 4.000 → fee 50,00 ( 1,25%)  total 4.981,50
//
//   A zero price stays zero — a free stand does not acquire a €1 fee.
//
// THE ERA BOUNDARY
//   Bookings made BEFORE the ceiling shipped were charged an uncapped 5%, so
//   anything that reads a breakdown back out of a stored total has to know
//   which rule that total was built with. Pass the booking's own date to
//   computeBookingPricingFromTotal / computeT2bFeeInvoiceFromTotal; without it
//   they assume the current (capped) era, which is right for new bookings and
//   wrong for old large ones.
//
//   T2B_FEE_CAP_FROM must match THERE2B_COMMISSION_CAP_FROM on the API (see
//   there-2-be-api/src/utils/commissionCap.js). If the API's value is changed
//   at deploy time, change it here too.
export const T2B_FEE_RATE = 0.05;
export const T2B_FEE_CAP = 50;
export const T2B_FEE_FLOOR = 1;
export const T2B_FEE_CAP_FROM = new Date('2026-08-24T00:00:00Z');
export const VAT_RATE = 0.23;

// Do the bounds apply to something dated `at`? No date means "now". Both
// bounds shipped together, so one date governs both.
const boundsApply = (at) => {
  if (at === null || at === undefined) return true;
  const d = at instanceof Date ? at : new Date(at);
  if (Number.isNaN(d.getTime())) return true;
  return d.getTime() >= T2B_FEE_CAP_FROM.getTime();
};

const feeCapFor = (at) => (boundsApply(at) ? T2B_FEE_CAP : Infinity);
const feeFloorFor = (at) => (boundsApply(at) ? T2B_FEE_FLOOR : 0);

// EUR → integer cents. Two-stage like the API's toCents: `n * 100` alone
// misreads values such as 1.005 (100.499…).
const toCents = (n) => Math.round(Math.round(n * 1000) / 10);
// Rate applied to a cents amount, rounded half-up to whole cents. Rates are
// held in basis points so the product is an exact integer before dividing.
const applyRate = (cents, rate) =>
  Math.round((cents * Math.round(rate * 10000)) / 10000);

export const computeBookingPricing = (spacePrice, { at } = {}) => {
  const price = Number(spacePrice);
  const priceC = Number.isFinite(price) && price > 0 ? toCents(price) : 0;
  // A free stand stays free: the floor applies to a positive price only.
  const feeC =
    priceC > 0
      ? Math.min(
          Math.max(applyRate(priceC, T2B_FEE_RATE), feeFloorFor(at) * 100),
          feeCapFor(at) * 100,
        )
      : 0;
  const priceVatC = applyRate(priceC, VAT_RATE);
  const feeVatC = applyRate(feeC, VAT_RATE);

  return {
    price: priceC / 100,
    t2bFee: feeC / 100,
    vat: (priceVatC + feeVatC) / 100,
    // Gross of the There2B fee invoice (fee + its own IVA).
    t2bFeeGross: (feeC + feeVatC) / 100,
    total: (priceC + priceVatC + feeC + feeVatC) / 100,
  };
};

export const computeBookingTotal = (spacePrice, opts) =>
  computeBookingPricing(spacePrice, opts).total;

// Inverse of computeBookingPricing. Piecewise because of the bounds — three
// regions, split at the two threshold subtotals (bound × (1 + rate) / rate,
// i.e. €21 and €1.050). In the flat regions the fee is known, so the price is a
// subtraction; between them the old division still holds. Monotonic and
// continuous throughout, so there is exactly one price per total.
export const computeBookingPricingFromTotal = (totalAmount, { at } = {}) => {
  const total = Number(totalAmount) || 0;
  const cap = feeCapFor(at);
  const floor = feeFloorFor(at);
  const subtotal = total / (1 + VAT_RATE);
  const capThreshold = Number.isFinite(cap)
    ? (cap * (1 + T2B_FEE_RATE)) / T2B_FEE_RATE
    : Infinity;
  const floorThreshold = floor > 0 ? (floor * (1 + T2B_FEE_RATE)) / T2B_FEE_RATE : 0;

  let price;
  if (subtotal <= floor) {
    // Degenerate (zero/near-zero total): no fee was charged, and subtracting
    // the floor would go negative.
    price = subtotal;
  } else if (subtotal < floorThreshold) {
    price = subtotal - floor;
  } else if (subtotal > capThreshold) {
    price = subtotal - cap;
  } else {
    price = subtotal / (1 + T2B_FEE_RATE);
  }
  return computeBookingPricing(price, { at });
};

// Gross of the There2B fee invoice: the fee (5% of the space price, bounded to
// €1–€50) plus VAT on it. Mirrors backend money.js commissionGrossCents — keep
// in sync.
export const computeT2bFeeInvoiceFromTotal = (totalAmount, opts) =>
  computeBookingPricingFromTotal(totalAmount, opts).t2bFeeGross;
